import { seatAddressOf, FEATURES, FEATURE_GROUPS, featureAvailable, formatMoney, isPlausibleEmail, REQUIRED_FIELDS, SETTINGS, fromMinor, isRequired, requiredFieldKey, toMinor, ADMIN_PERMISSIONS, DEFAULT_PERMISSIONS, MECHANIC_SWITCHES, withMechanicSwitch, type RequiredEntity, type Role } from "@szerviz/core";
import { Fragment, useCallback, useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { DataExport } from "../components/DataExport";
import { SmsSettings } from "../components/SmsSettings";
import { ReminderSettingsPanel } from "../components/Reminders";
import { AiCreditPanel } from "../components/AiCreditPanel";
import { InvoicingSettings } from "../components/PaymentInvoice";
import { ErrorText } from "../components/ErrorText";
import { Help } from "../components/Help";
import { LogoSettings } from "../components/WorkshopLogo";
import { ReadyNoticeSettings } from "../components/ReadyNoticeSettings";
import { can, useApp, useCan, useFeature } from "../context";
import { IntakeSettings } from "../components/Intake";
import type { Member, SessionUser } from "../data";
import { PasswordForm } from "./SetPassword";
import { MfaSettings } from "./MfaSettings";
import { SubscriptionNotes, useSubscription } from "./Subscription";
import { PlanTag } from "../components/TrialGuide";

export function Settings({ user }: { user: SessionUser }) {
  const { t } = useTranslation();
  const { store, workshop, go, role: myRole } = useApp();
  const [members, setMembers] = useState<Member[]>([]);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  // the usual new colleague is a mechanic (owner, 2026-11-04)
  const [role, setRole] = useState<Role>("mechanic");
  const [error, setError] = useState<unknown>(null);
  const [info, setInfo] = useState<string | null>(null);

  const [seats, setSeats] = useState<{ used: number; limit: number } | null>(null);
  const load = useCallback(async () => {
    setMembers(await store.listMembers(workshop.id));
    setSeats(await store.seats(workshop.id));
  }, [store, workshop.id]);
  useEffect(() => void load(), [load]);

  const dataExportOn = useFeature("dataExport");
  // the workshop's settings, the users and the subscription are the owner's (2026-11-04); the others see their own settings
  const isOwner = myRole === "owner";
  const canBuyAi = useCan("aiBuy");
  const me = members.find((m) => m.userId === user.id);
  const { sub } = useSubscription();

  const run = async (fn: () => Promise<unknown>, message?: string) => {
    setError(null);
    setInfo(null);
    try {
      await fn();
      if (message) setInfo(message);
    } catch (err) {
      setError(err);
    }
    await load();
  };

  // the e-mail address is required and must look right before the invitation can go (#125)
  const inviteMissing = email.trim() ? [] : [t("users.email")];
  const inviteEmailBad = email.trim() !== "" && !isPlausibleEmail(email);
  const unlimited = !seats || seats.limit >= 100000;
  const seatsFull = !!seats && !unlimited && seats.used >= seats.limit;

  const invite = async (e: FormEvent) => {
    e.preventDefault();
    if (inviteMissing.length > 0 || inviteEmailBad || seatsFull) return;
    await run(async () => {
      const status = await store.inviteMember(workshop.id, email, role);
      // the name shown in the pickers and on the printed work order
      if (name.trim()) {
        const added = (await store.listMembers(workshop.id)).find((m) => m.email.toLowerCase() === email.trim().toLowerCase());
        if (added) await store.setMemberName(workshop.id, added.userId, name);
      }
      setInfo(t(status === "invited" ? "users.invited" : "users.added", { email: email.trim() }));
      setEmail("");
      setName("");
    });
  };

  return (
    <section className="narrow settings">
      <h1>{t("nav.settings")}</h1>

      {!isOwner && (
        <>
          <p className="note">{t("settings.ownerOnly")}</p>
          {me && (
            <label className="field own-name">
              <span className="field-label">{t("users.ownName")}</span>
              <MemberNameInput key={`${me.userId}-${me.displayName ?? ""}`} member={me} onSave={(v) => run(() => store.setMemberName(workshop.id, me.userId, v), t("common.saved"))} />
              <small>{t("users.nameHint")}</small>
            </label>
          )}
          <ErrorText error={error} />
          {info && <p className="info">{info}</p>}
          {canBuyAi && <AiCreditPanel canBuy />}
        </>
      )}

      {isOwner && <>
      <div className="sub-summary" id="subscription">
        <h2>{t("settings.subscriptionTitle")}</h2>
        {sub && <SubscriptionNotes sub={sub} />}
        <button type="button" className="primary" onClick={() => go("subscription")}>
          {t("subscription.open")}
        </button>
      </div>

      <h2>{t("users.title")}</h2>
      <p className="muted">{t("users.introOwner")}</p>
      {seats && (
        <div className={`seat-meter${seatsFull ? " full" : ""}`} role="status">
          <strong>{unlimited ? t("users.seatsUnlimited", { used: seats.used }) : t("users.seatsUsedFree", { used: seats.used, limit: seats.limit, free: Math.max(0, seats.limit - seats.used) })}</strong>
          {!unlimited && (
            <span className="seat-dots" aria-hidden="true">
              {Array.from({ length: Math.min(seats.limit, 20) }, (_, i) => <i key={i} className={i < seats.used ? "used" : undefined} />)}
            </span>
          )}
          <Help id="users.seats" />
        </div>
      )}
      <div className="table-scroll">
      <table className="list users-list">
        <thead>
          <tr>
            <th>{t("users.name")}</th>
            <th>{t("users.email")}</th>
            <th>{t("users.role")}</th>
            {isOwner && <th />}
          </tr>
        </thead>
        <tbody>
          {members.map((m) => (
            <tr key={m.userId}>
              <td data-label={t("users.name")}>
                {isOwner || m.userId === user.id ? (
                  <MemberNameInput key={`${m.userId}-${m.displayName ?? ""}`} member={m} onSave={(v) => run(() => store.setMemberName(workshop.id, m.userId, v), t("common.saved"))} />
                ) : (
                  m.displayName ?? <span className="muted">–</span>
                )}
              </td>
              <td data-label={t("users.email")}>
                {m.email} {m.userId === user.id && <span className="muted">({t("users.you")})</span>}
              </td>
              <td>
                {isOwner ? (
                  // the three levels as small cards with a round selector (either-or choice, not a dropdown)
                  <fieldset className="role-pick" aria-label={`${t("users.role")}: ${m.email}`}>
                    {INVITE_ROLES.map((r) => (
                      <label key={r} className={`role-chip${m.role === r ? " selected" : ""}`}>
                        <input type="radio" name={`role-${m.userId}`} checked={m.role === r} onChange={() => void run(() => store.updateMemberRole(workshop.id, m.userId, r))} />
                        {t(`role.${r}.label`)}
                      </label>
                    ))}
                  </fieldset>
                ) : (
                  t(`role.${m.role}.label`)
                )}
              </td>
              {isOwner && (
                <td className="row-actions">
                  <button
                    type="button"
                    className="danger"
                    aria-label={`${t("users.remove")}: ${m.email}`}
                    onClick={() => {
                      if (globalThis.confirm?.(t("common.confirmDelete"))) void run(() => store.removeMember(workshop.id, m.userId));
                    }}
                  >
                    {t("users.remove")}
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      <ErrorText error={error} />
      {info && <p className="info">{info}</p>}

      {isOwner && (
        <form className="form-grid" onSubmit={invite} noValidate>
          <h3 className="wide">{t("users.inviteTitle")}</h3>
          <label className={`field${inviteMissing.length > 0 || inviteEmailBad ? " invalid" : ""}`}>
            <span>{t("users.email")}</span>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} aria-label={t("users.inviteEmail")} aria-invalid={inviteMissing.length > 0 || inviteEmailBad || undefined} />
          </label>
          <label className="field">
            <span>{t("users.name")}</span>
            <input value={name} onChange={(e) => setName(e.target.value)} aria-label={t("users.inviteName")} maxLength={80} />
            <small>{t("users.nameHint")}</small>
          </label>
          {/* the role as cards with what each may do; Szerelő first, the usual new colleague (owner, 2026-11-04) */}
          <fieldset className="choice-cards role-cards wide">
            <legend>
              {t("users.inviteRole")} <Help id="users.roles" />
            </legend>
            {INVITE_ROLES.map((r) => (
              <label key={r} className={`choice-card${role === r ? " selected" : ""}`}>
                <input type="radio" name="inviteRole" checked={role === r} onChange={() => setRole(r)} />
                <span>
                  <strong>{t(`role.${r}.label`)}</strong>
                  <br />
                  <small className="muted">{t(`role.${r}.description`)}</small>
                </span>
              </label>
            ))}
          </fieldset>
          {role === "mechanic" && <p className="muted wide">{t("users.mechanicNote")}</p>}
          <div className="actions wide">
            <button type="submit" className="primary" disabled={inviteMissing.length > 0 || inviteEmailBad || seatsFull}>{t("users.invite")}</button>
            {seatsFull && <span className="missing" role="status">{t("error.SEATS_FULL.message")} {t("error.SEATS_FULL.action")}</span>}
            {inviteMissing.length > 0 && <span className="missing" role="status">{t("required.stillMissing", { fields: inviteMissing.join(", ") })}</span>}
            {inviteEmailBad && <span className="missing" role="status">{t("error.INVALID_EMAIL.message")}</span>}
          </div>
        </form>
      )}

      <FeatureSettings canEdit />

      <SmsSettings isOwner canEdit />
      <ReminderSettingsPanel canEdit />
      <InvoicingSettings isOwner />
      <AiCreditPanel canBuy />

      <LogoSettings canEdit />

      <GeneralSettings canEdit />

      <ReadyNoticeSettings canEdit />

      <RequiredFieldsSettings canEdit />

      <PermissionSettings />

      {dataExportOn && <DataExport />}
      </>}

      <h2>{t("help.settingsTitle")}</h2>
      <HelpSwitch />

      <h2>{t("legal.page.title")}</h2>
      <p className="muted">{t("legal.page.intro")}</p>
      <button type="button" onClick={() => go("legal")}>
        {t("legal.page.open")}
      </button>

      <h2>{t("users.passwordTitle")}</h2>
      <PasswordForm store={store} compact />

      <MfaSettings user={user} isOwner={isOwner} />
    </section>
  );
}

/** The three levels (owner, 2026-11-04): mechanics first in the invite cards, the owner last (a second owner is rare). */
const INVITE_ROLES: readonly Role[] = ["mechanic", "manager", "owner"];

/** A member's name: saved when the field is left (empty: the e-mail address is shown). */
function MemberNameInput({ member, onSave }: { member: Member; onSave: (name: string) => Promise<void> }) {
  const { t } = useTranslation();
  const [value, setValue] = useState(member.displayName ?? "");
  return (
    <input
      className="member-name"
      value={value}
      maxLength={80}
      placeholder={member.email.split("@")[0]}
      aria-label={`${t("users.name")}: ${member.email}`}
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => {
        if (value.trim() !== (member.displayName ?? "")) void onSave(value);
      }}
    />
  );
}

function HelpSwitch() {
  const { t } = useTranslation();
  const { helpEnabled, setHelpEnabled } = useApp();
  return (
    <label className="switch-row">
      <span>
        <strong>{t("help.switch")}</strong>
        <br />
        <span className="muted">{t("help.settingsText")}</span>
      </span>
      <input type="checkbox" checked={helpEnabled} onChange={(e) => void setHelpEnabled(e.target.checked)} aria-label={t("help.switch")} />
    </label>
  );
}

/** Switchable features as cards: the package decides what can be switched on. */
function FeatureSettings({ canEdit }: { canEdit: boolean }) {
  const { t, i18n } = useTranslation();
  const { store, workshop, reloadWorkshop } = useApp();
  const [error, setError] = useState<unknown>(null);
  // a paid add-on is switched on only after its price has been accepted once
  const [accepting, setAccepting] = useState<string | null>(null);
  // one change at a time, so a quick second click does not overwrite the first
  const [saving, setSaving] = useState(false);

  const toggle = async (key: string, on: boolean) => {
    setError(null);
    if (on && FEATURES.find((f) => f.key === key)?.price && accepting !== key) {
      setAccepting(key);
      return;
    }
    setAccepting(null);
    setSaving(true);
    try {
      await store.saveFeatures(workshop.id, on ? [...workshop.features, key] : workshop.features.filter((f) => f !== key));
      await reloadWorkshop();
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <h2>{t("features.title")}</h2>
      <p className="muted">{t("features.intro", { plan: t(`plan.${workshop.plan}.label`) })}</p>
      <ErrorText error={error} />
      {FEATURE_GROUPS.filter((g) => FEATURES.some((f) => f.group === g)).map((g) => (
        <div key={g} className="feature-group">
          <h3>{t(`features.group.${g}`)}</h3>
          {FEATURES.filter((f) => f.group === g).map((f) => {
            const available = featureAvailable(workshop.plan, f.key, workshop.extraFeatures);
            const on = available && workshop.features.includes(f.key);
            return (
              <Fragment key={f.key}>
              <div className={`feature-card${available ? "" : " locked"}`}>
              <label className="feature-row">
                <span>
                  <strong>{t(`feature.${f.key}.label`)}</strong> <PlanTag featureKey={f.key} />
                  <br />
                  <span className="muted">{t(`feature.${f.key}.description`)}</span>
                  {f.price && (
                    <>
                      <br />
                      <span className="paid-note">{t("feature.paidPerSms", { price: formatMoney(f.price.amount, f.price.currency, i18n.language) })}</span>
                    </>
                  )}
                  {!available && (
                    <>
                      <br />
                      <span className="plan-note">{t("features.inPlan", { plan: t(`plan.${f.plan}.label`) })}</span>
                    </>
                  )}
                </span>
                <input
                  type="checkbox"
                  checked={on}
                  disabled={!canEdit || !available || saving}
                  aria-label={t(`feature.${f.key}.label`)}
                  onChange={(e) => void toggle(f.key, e.target.checked)}
                />
              </label>
              {/* the intake's own choices sit right under its switch, while it is on (owner's design, 2026-11-04) */}
              {f.key === "intakePhotos" && on && <IntakeSettings canEdit={canEdit} />}
              <FeatureDetails featureKey={f.key} />
              </div>
              {accepting === f.key && f.price && (
                <div className="paid-accept" role="group" aria-label={t(`feature.${f.key}.label`)}>
                  <p className="warn">{t("feature.paidConfirm", { price: formatMoney(f.price.amount, f.price.currency, i18n.language) })}</p>
                  <div className="actions">
                    <button type="button" className="primary" onClick={() => void toggle(f.key, true)}>
                      {t("feature.paidAccept")}
                    </button>
                    <button type="button" onClick={() => setAccepting(null)}>
                      {t("common.cancel")}
                    </button>
                  </div>
                </div>
              )}
              </Fragment>
            );
          })}
        </div>
      ))}
    </>
  );
}

/**
 * "More": what a switchable feature is good for (feature.<key>.details), opened on request.
 * Not tied to the help switch: the owner has to be able to read it before switching anything on.
 */
function FeatureDetails({ featureKey }: { featureKey: string }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const f = FEATURES.find((x) => x.key === featureKey)!;
  const id = `feature-details-${featureKey}`;
  return (
    <>
      <button type="button" className="link feature-more-toggle" aria-expanded={open} aria-controls={id} onClick={() => setOpen((o) => !o)}>
        {t(open ? "features.less" : "features.more")}
      </button>
      {open && (
        <div className="feature-more" id={id} role="region" aria-label={t("features.moreTitle")}>
          <strong>{t("features.moreTitle")}</strong>
          <p>
            {t(`feature.${featureKey}.details`, {
              plan: t(`plan.${f.plan}.label`),
              price: f.price ? formatMoney(f.price.amount, f.price.currency, i18n.language) : "",
            })}
          </p>
        </div>
      )}
    </>
  );
}

const ENTITIES: RequiredEntity[] = ["workOrder", "customer", "vehicle"];

function RequiredFieldsSettings({ canEdit }: { canEdit: boolean }) {
  const { t } = useTranslation();
  const { store, workshop, reloadWorkshop } = useApp();
  const [error, setError] = useState<unknown>(null);
  const choices = workshop.requiredFields;

  const save = async (next: Record<string, boolean>) => {
    setError(null);
    try {
      await store.saveRequiredFields(workshop.id, next);
      await reloadWorkshop();
    } catch (err) {
      setError(err);
    }
  };

  return (
    <>
      <div className="page-head">
        <h2>
          {t("required.title")} <Help id="required.page" />
        </h2>
        {canEdit && (
          <button type="button" onClick={() => void save({})}>
            {t("required.reset")}
          </button>
        )}
      </div>
      <p className="muted">{t(canEdit ? "required.intro" : "required.introReadOnly")}</p>
      <ErrorText error={error} />
      {ENTITIES.map((entity) => (
        <div key={entity} className={`req-group req-${entity}`}>
          <h3>{t(`required.group.${entity}`)}</h3>
          {REQUIRED_FIELDS.filter((d) => d.entity === entity).map((d) => {
            const key = requiredFieldKey(d);
            const on = isRequired(d, choices);
            return (
              <label key={key} className="switch-row">
                <span>
                  <strong>{t(d.labelKey)}</strong>
                </span>
                {d.locked ? (
                  <span className="badge-lock">
                    <svg className="gi" aria-hidden="true" width="18" height="18" viewBox="0 0 48 48" focusable="false"><path d="M14 22v-8a10 10 0 0 1 20 0v8" fill="none" strokeWidth="6" style={{ stroke: "var(--ico-body, #0E2433)" }} /><rect x="8" y="22" width="32" height="22" style={{ fill: "var(--ico-body, #0E2433)" }} /><rect x="21" y="29" width="6" height="8" style={{ fill: "var(--ico-accent, #10B89A)" }} /></svg>
                    {t("required.always")}
                  </span>
                ) : (
                  <>
                    <span className={on ? "state-on" : "state-off"}>{t(on ? "required.on" : "required.off")}</span>
                    <input
                      type="checkbox"
                      checked={on}
                      disabled={!canEdit}
                      aria-label={`${t("required.title")}: ${t(d.labelKey)}`}
                      onChange={(e) => void save({ ...choices, [key]: e.target.checked })}
                    />
                  </>
                )}
              </label>
            );
          })}
        </div>
      ))}
    </>
  );
}

function GeneralSettings({ canEdit }: { canEdit: boolean }) {
  const { t } = useTranslation();
  const { store, workshop, reloadWorkshop } = useApp();
  // the SMS sender is chosen in the SMS sending panel, the reminders in their own panel
  // two-step sign in for managers is the owner's choice, in its own panel
  // the intake settings sit under the "Átvétel fotókkal" option; the "car ready" notice and the opening hours have their own panel
  const shown = SETTINGS.filter((d) => d.key !== "currency" && d.key !== "smsProvider" && d.key !== "mfaManagers" && !d.key.startsWith("reminder") && !d.key.startsWith("intakePhoto")
    && !d.key.startsWith("readyNotice") && !d.key.startsWith("openingHours"));
  const initial = Object.fromEntries(
    shown.map((d) => {
      // a workshop from before the two addresses has only workshopAddress: that is its seat
      const v = d.key === "seatAddress" ? seatAddressOf(workshop.general) : workshop.general[d.key] ?? d.default;
      return [d.key, v === null ? "" : d.type === "money" ? String(fromMinor(Number(v), workshop.currency)) : d.type === "switch" ? String(v === true || v === "true") : String(v)];
    }),
  );
  const [values, setValues] = useState<Record<string, string>>(initial);
  const [error, setError] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaved(false);
    const out: Record<string, number | string | boolean | null> = { ...workshop.general };
    // the workshop address follows the seat while the tick is on (older readers use workshopAddress)
    const vals = values.workshopAddressSameAsSeat === "true" ? { ...values, workshopAddress: values.seatAddress ?? "" } : values;
    for (const d of shown) {
      const typed = (vals[d.key] ?? "").trim();
      const raw = d.type === "money" || d.type === "percent" ? typed.replace(",", ".") : typed;
      if (d.type === "money") out[d.key] = raw === "" ? null : toMinor(Number(raw), workshop.currency);
      else if (d.type === "percent") out[d.key] = raw === "" ? null : Number(raw);
      else if (d.type === "switch") out[d.key] = raw === "true";
      else out[d.key] = raw || null;
    }
    try {
      await store.saveGeneralSettings(workshop.id, out);
      await reloadWorkshop();
      setSaved(true);
    } catch (err) {
      setError(err);
    }
  };

  return (
    <>
      <h2>{t("settings.generalTitle")}</h2>
      <form className="form-grid" onSubmit={save} noValidate>
        {shown.filter((d) => !(d.key === "workshopAddress" && values.workshopAddressSameAsSeat === "true")).map((d) =>
          d.type === "switch" ? (
            <label key={d.key} className="field switch-field">
              <span className="field-label">{t(`setting.${d.key}.label`)}</span>
              <span className="switch-row">
                <input
                  type="checkbox"
                  checked={values[d.key] === "true"}
                  disabled={!canEdit}
                  aria-label={t(`setting.${d.key}.label`)}
                  onChange={(e) => setValues((v) => ({ ...v, [d.key]: String(e.target.checked) }))}
                />
                <span>{values[d.key] === "true" ? t("manual.default.on") : t("manual.default.off")}</span>
              </span>
              <small>{t(`setting.${d.key}.description`)}</small>
            </label>
          ) : d.type === "select" ? (
            <label key={d.key} className="field">
              <span className="field-label">{t(`setting.${d.key}.label`)}</span>
              <select
                value={values[d.key] ?? ""}
                disabled={!canEdit}
                aria-label={t(`setting.${d.key}.label`)}
                onChange={(e) => setValues((v) => ({ ...v, [d.key]: e.target.value }))}
              >
                {(d.options ?? []).map((o) => (
                  <option key={o} value={o}>
                    {t(`setting.${d.key}.option.${o}`)}
                  </option>
                ))}
              </select>
              <small>{t(`setting.${d.key}.description`)}</small>
            </label>
          ) : d.type === "longtext" ? (
            <label key={d.key} className="field wide">
              <span className="field-label">{t(`setting.${d.key}.label`)}</span>
              <textarea
                rows={3}
                value={values[d.key] ?? ""}
                disabled={!canEdit}
                aria-label={t(`setting.${d.key}.label`)}
                onChange={(e) => setValues((v) => ({ ...v, [d.key]: e.target.value }))}
              />
              <small>{t(`setting.${d.key}.description`)}</small>
            </label>
          ) : (
          <label key={d.key} className="field">
            <span className="field-label">
              {t(`setting.${d.key}.label`)}
              {d.type === "money" && ` (${workshop.currency})`}
              {(d.key === "seatAddress" || d.key === "workshopAddress") && <Help id="settings.addresses" />}
            </span>
            <input
              value={values[d.key] ?? ""}
              disabled={!canEdit}
              inputMode={d.type === "text" ? "text" : "decimal"}
              aria-label={t(`setting.${d.key}.label`)}
              onChange={(e) => setValues((v) => ({ ...v, [d.key]: e.target.value }))}
            />
            <small>{t(`setting.${d.key}.description`)}</small>
          </label>
          ),
        )}
        {canEdit && (
          <div className="actions wide">
            <ErrorText error={error} />
            {saved && <span className="ok">{t("common.saved")}</span>}
            <button type="submit" className="primary">
              {t("common.save")}
            </button>
          </div>
        )}
      </form>
    </>
  );
}

/**
 * Who may do what, the owner's page (2026-11-04): the two switches for mechanics (off by default) and the
 * administrator's two work order rights. The database checks the same (public.can_do, has_permission).
 */
function PermissionSettings() {
  const { t } = useTranslation();
  const { store, workshop, reloadWorkshop } = useApp();
  const [error, setError] = useState<unknown>(null);
  const current = (p: string) => workshop.permissions[p] ?? DEFAULT_PERMISSIONS[p] ?? ["owner"];
  const save = async (next: Record<string, Role[]>) => {
    setError(null);
    try {
      await store.savePermissions(workshop.id, next);
      await reloadWorkshop();
    } catch (err) {
      setError(err);
    }
  };
  const toggleAdmin = (p: string, on: boolean) => {
    const next = new Set(current(p));
    if (on) next.add("manager");
    else next.delete("manager");
    next.add("owner");
    void save({ ...workshop.permissions, [p]: INVITE_ROLES.filter((r) => r !== "mechanic" && next.has(r)) });
  };
  const switchOn = (key: keyof typeof MECHANIC_SWITCHES) => can("mechanic", workshop, MECHANIC_SWITCHES[key]);
  return (
    <>
      <h2>
        {t("permissions.title")} <Help id="permissions.page" />
      </h2>
      <p className="muted">{t("permissions.intro")}</p>
      <ErrorText error={error} />
      <h3>{t("permissions.mechanicTitle")}</h3>
      <div className="perm-switches">
        {(["items", "prices"] as const).map((key) => (
          <div key={key} className="switch-row perm-switch">
            <label>
              <input type="checkbox" checked={switchOn(key)} onChange={(e) => void save(withMechanicSwitch(workshop.permissions, key, e.target.checked))} />
              <strong>{t(`permissions.mechanic.${key}.label`)}</strong>
            </label>
            <details className="why">
              <summary>{t("permissions.why")}</summary>
              <p>{t(`permissions.mechanic.${key}.why`)}</p>
            </details>
          </div>
        ))}
      </div>
      <h3>{t("permissions.adminTitle")}</h3>
      {/* one row per action, no table: fits the phone without side scrolling (the owner always may) */}
      <div className="perm-switches perm-admin">
        {ADMIN_PERMISSIONS.map((p) => (
          <div key={p} className="switch-row perm-switch">
            <label>
              <input
                type="checkbox"
                checked={current(p).includes("manager")}
                aria-label={`${t(`permission.${p}`)}: ${t("role.manager.label")}`}
                onChange={(e) => toggleAdmin(p, e.target.checked)}
              />
              <span>
                <strong>{t(`permission.${p}`)}</strong>
                <br />
                <small className="muted">{t("role.manager.label")} · {t("role.owner.label")} ✓</small>
              </span>
            </label>
          </div>
        ))}
      </div>
    </>
  );
}
